import { useEffect, useMemo, useRef } from 'react';
import useI18n from '../../hooks/useI18n';
import { UserContext } from '../../context/UserContext';
import { useContext } from 'react';
import { useMinimizeContext } from '@/context/LayoutContext';

interface ActionsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
  userStatus: string;
  userRole: string;
  userId: string;
  currentUserRole: string;
  isLoading: boolean;
  setIsLoading: (arg: boolean) => void;
  setIsEditRoleModalOpen: (arg: boolean) => void;
  setIsDeleteModalOpen: (arg: boolean) => void;
  setIsDeactivateModalOpen: (arg: boolean) => void;
  setIsReInviteModalOpen: (arg: boolean) => void;
}

const ActionsPopup: React.FC<ActionsPopupProps> = ({
  isOpen,
  onClose,
  position,
  userStatus,
  userRole,
  userId,
  currentUserRole,
  isLoading,
  setIsEditRoleModalOpen,
  setIsDeleteModalOpen,
  setIsDeactivateModalOpen,
  setIsReInviteModalOpen,
}) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  const popupRef = useRef<HTMLDivElement>(null);
  const { minimize, setMinimize } = useMinimizeContext();

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;
  const isSuperAdmin = useMemo(() => user?.email === user?.brand.email, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.icon-button')
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleAction = (action: string) => {
    onClose();
    if (action === 'edit-role') {
      setIsEditRoleModalOpen(true);
    } else if (action === 'deactivate' || action === 'reactivate') {
      setIsDeactivateModalOpen(true);
    } else if (action === 'reinvite') {
      setIsReInviteModalOpen(true);
    } else {
      setIsDeleteModalOpen(true);
    }
  };

  if (!isOpen) return null;

  const isCurrentUserAdmin = currentUserRole === 'admin';
  const isCurrentUserManager = currentUserRole === 'manager';
  const isTargetUserAdmin = userRole === 'admin';
  const isTargetUserManager = userRole === 'manager';
  const isTargetUserRegular = userRole === 'user';
  const isCurrentUser = user?._id === userId;

  // Super Admin permissions
  const superAdminCanEditRole = isSuperAdmin;
  const superAdminCanDeactivate = isSuperAdmin && userStatus !== 'pending';
  const superAdminCanDelete = isSuperAdmin;
  const superAdminCanReinvite = isSuperAdmin && userStatus === 'pending';

  // Admin permissions
  const adminCanReinvite =
    isCurrentUserAdmin && userStatus === 'pending' && !isCurrentUser;
  const adminCanEditRole =
    isCurrentUserAdmin &&
    (isCurrentUser ||
      (!isTargetUserAdmin && (isTargetUserManager || isTargetUserRegular)));
  const adminCanDeactivate =
    isCurrentUserAdmin &&
    (isCurrentUser ||
      (!isTargetUserAdmin && (isTargetUserManager || isTargetUserRegular))) &&
    userStatus !== 'pending';
  const adminCanDelete =
    isCurrentUserAdmin &&
    (isCurrentUser ||
      (!isTargetUserAdmin && (isTargetUserManager || isTargetUserRegular)));

  // Manager permissions
  const managerCanReinvite =
    isCurrentUserManager &&
    (isTargetUserManager || isTargetUserRegular) &&
    userStatus === 'pending';
  const managerCanEditRole =
    (isCurrentUserManager && isCurrentUser) || isTargetUserRegular;
  const managerCanDeactivate =
    isCurrentUserManager &&
    (isCurrentUser || isTargetUserRegular) &&
    userStatus !== 'pending';
  const managerCanDelete = false;

  // Combine permissions
  const canEditRole =
    superAdminCanEditRole || adminCanEditRole || managerCanEditRole;
  const canDeactivate =
    superAdminCanDeactivate || adminCanDeactivate || managerCanDeactivate;
  const canDelete = superAdminCanDelete || adminCanDelete || managerCanDelete;
  const canReinvite =
    superAdminCanReinvite || adminCanReinvite || managerCanReinvite;

  return (
    <>
      {isOpen && (
        <div
          ref={popupRef}
          className="absolute z-50 bg-white shadow-lg rounded-md border border-gray-200 w-[134px]"
          style={{
            top: `${position.top - 100}px`,
            left: minimize
              ? `${position.left - 235}px`
              : `${position.left - 450}px`,
          }}
        >
          <div>
            {canReinvite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction('reinvite');
                }}
                disabled={isLoading}
                className={`w-full text-center pt-5 pb-4 py-4 text-base font-normal hover:bg-[#F8F8F8] text-[#292828] border-b ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('userManagement.actions.reInvite')}
              </button>
            )}

            {canEditRole && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction('edit-role');
                }}
                disabled={isLoading}
                className={`w-full text-center pt-5 pb-4 py-4 text-base font-normal hover:bg-[#F8F8F8] text-[#292828] border-b ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('userManagement.actions.editRole')}
              </button>
            )}

            {canDeactivate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction(
                    userStatus === 'active' ? 'deactivate' : 'reactivate',
                  );
                }}
                disabled={isLoading || userStatus === 'pending'}
                className={`w-full text-center px-4 py-4 text-base font-normal hover:bg-[#F8F8F8] text-[#292828] border-b disabled:cursor-not-allowed ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {userStatus === 'deactive'
                  ? t('userManagement.actions.reactivate')
                  : t('userManagement.actions.deactivate')}
              </button>
            )}

            {canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction('delete');
                }}
                disabled={isLoading}
                className={`w-full text-center pt-4 pb-5 py-4 text-base font-normal hover:bg-[#F8F8F8] text-[#292828] ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('userManagement.actions.delete')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ActionsPopup;
